import { GpsPoint, HalfEvent } from "refsix-js-models";
import { GpsProcessingService } from "./GpsProcessingService";

// Top speed for a match (SCORZA-540).
//
// A single point-to-point speed is far too noisy to publish as "your top
// speed" — one jittery fix inflates it. Instead the ticket specifies the
// average speed across a rolling 5-point GPS window, and top speed is the
// fastest such window. Window speed is total distance over total elapsed
// time rather than the mean of the individual point speeds: with uneven
// sampling the mean over-weights the short intervals, and a dropout inside
// a window then reads as a sprint.
//
// Runs against the smoothed track produced by gpsSmoothing (SCORZA-730), so
// the per-point positions are already free of the spikes the old percentile
// filter let through.

export const TOP_SPEED_WINDOW_POINTS = 5;

/**
 * Longest wall-clock span a window may cover, in seconds.
 *
 * The window is defined in samples, but the quantity it reports is a speed
 * over time — and the two only agree while sampling is regular. Runs are
 * broken at playing-segment boundaries, not at GPS dropouts, so five
 * consecutive *retained* points can straddle a mid-half blackout and span half
 * a minute. Such a window reports a long-run average dressed as a four-second
 * peak: it hides a genuine sprint when the referee slowed during the gap, and
 * overstates the peak when they sprinted through it.
 *
 * At the nominal 1Hz a window is 4s, so this allows roughly double that —
 * enough to ride out a few dropped samples without discarding real windows,
 * and short enough that a dropout can no longer masquerade as a peak.
 */
export const TOP_SPEED_MAX_WINDOW_SEC = 8;

export interface TopSpeedResult {
    /** Fastest window across the whole match, in metres per second. */
    overall: number;
    /** Fastest window within each playing segment, indexed as matchTimings. */
    bySegment: number[];
}

/**
 * Fastest 5-point window overall and per playing segment.
 *
 * Windows never span a segment boundary, so half time can't be averaged into
 * a half's top speed, and never span more than TOP_SPEED_MAX_WINDOW_SEC, so a
 * mid-half GPS dropout can't either. A segment with fewer than
 * TOP_SPEED_WINDOW_POINTS points yields 0 — there is no window to measure.
 */
export function calculateTopSpeed(
    geoPoints: GpsPoint[],
    matchTimings: HalfEvent[]
): TopSpeedResult {
    const gpsProcessingService = new GpsProcessingService();
    const bySegment: number[] = matchTimings.map(() => 0);
    let overall = 0;

    let run: GpsPoint[] = [];
    let runSegment: number | undefined;

    const finishRun = () => {
        if (runSegment !== undefined) {
            const fastest = fastestWindow(run, gpsProcessingService);
            bySegment[runSegment] = Math.max(bySegment[runSegment], fastest);
            overall = Math.max(overall, fastest);
        }
        run = [];
    };

    geoPoints.forEach((point) => {
        const segment = gpsProcessingService._minuteOffsetToSegment(
            matchTimings,
            point.time
        );
        // Points outside play have no segment; they end the current run rather
        // than joining it, so a window never bridges a stoppage.
        if (segment !== runSegment) {
            finishRun();
            runSegment = segment;
        }
        run.push(point);
    });
    finishRun();

    return { overall, bySegment };
}

/**
 * Highest average speed over any TOP_SPEED_WINDOW_POINTS-long window of a
 * single continuous run of points, in metres per second.
 */
function fastestWindow(
    points: GpsPoint[],
    gpsProcessingService: GpsProcessingService
): number {
    if (points.length < TOP_SPEED_WINDOW_POINTS) {
        return 0;
    }

    // Step i is the distance from points[i] to points[i + 1]. Pre-computing
    // them lets the window slide by one add and one subtract instead of
    // re-measuring every leg.
    const steps: number[] = [];
    for (let i = 1; i < points.length; i++) {
        steps.push(
            gpsProcessingService._distanceBetweenPoints(
                points[i - 1],
                points[i]
            )
        );
    }

    const stepsPerWindow = TOP_SPEED_WINDOW_POINTS - 1;
    let distance = 0;
    for (let i = 0; i < stepsPerWindow; i++) {
        distance += steps[i];
    }
    let fastest = windowSpeed(distance, points[0], points[stepsPerWindow]);

    for (let end = stepsPerWindow + 1; end < points.length; end++) {
        distance += steps[end - 1] - steps[end - 1 - stepsPerWindow];
        fastest = Math.max(
            fastest,
            windowSpeed(distance, points[end - stepsPerWindow], points[end])
        );
    }

    return fastest;
}

function windowSpeed(
    distance: number,
    first: GpsPoint,
    last: GpsPoint
): number {
    const elapsedSeconds = ((last.time || 0) - (first.time || 0)) / 1000;
    if (elapsedSeconds <= 0 || elapsedSeconds > TOP_SPEED_MAX_WINDOW_SEC) {
        return 0;
    }
    return distance / elapsedSeconds;
}
